import React, { useState } from 'react';
import { FormTreeData } from '@/features/submission_forms/types';
import { QuestionNode } from './QuestionNode';
import {
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  GripVertical,
  Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SectionNodeProps {
  section: FormTreeData;
  path: number[];
  selectedId?: string;
  depth?: number;
  onSelect: (id: string, path: number[]) => void;
  onSelectQuestion: (id: string, path: number[]) => void;
  onMoveSection: (fromPath: number[], toPath: number[]) => void;
  onMoveQuestion: (
    sectionPath: number[],
    fromIdx: number,
    toIdx: number
  ) => void;
  onAddQuestion: (sectionPath: number[]) => void;
  readOnly?: boolean;
}

export const SectionNode: React.FC<SectionNodeProps> = ({
  section,
  path,
  selectedId,
  depth = 0,
  onSelect,
  onSelectQuestion,
  onMoveSection,
  onMoveQuestion,
  onAddQuestion,
  readOnly = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const isSelected = selectedId === section.id;

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('section-path', JSON.stringify(path));
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const fromPathStr = e.dataTransfer.getData('section-path');
    if (fromPathStr) {
      const fromPath = JSON.parse(fromPathStr);
      // For now, only support same-level moves
      if (fromPath.length === path.length) {
        onMoveSection(fromPath, path);
      }
    }
  };

  const handleAddQuestion = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddQuestion(path);
  };

  return (
    <div
      className={`select-none ${isDragging ? 'opacity-50' : ''}`}
      style={{ marginLeft: `${depth * 20}px` }}
    >
      {/* Section Header */}
      <div
        draggable={!readOnly}
        onDragStart={readOnly ? undefined : handleDragStart}
        onDragEnd={readOnly ? undefined : handleDragEnd}
        onDragOver={readOnly ? undefined : handleDragOver}
        onDrop={readOnly ? undefined : handleDrop}
        onClick={() => onSelect(section.id, path)}
        className={`
          flex items-center gap-2 p-2 rounded-lg cursor-pointer
          hover:bg-gray-100 transition-colors group
          ${isSelected ? 'bg-blue-50 border border-blue-300' : 'border border-transparent'}
        `}
      >
        {/* Drag Handle */}
        {!readOnly && (
          <GripVertical className="w-4 h-4 text-gray-400 cursor-grab active:cursor-grabbing" />
        )}

        {/* Expand/Collapse */}
        <button
          onClick={e => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="p-0 hover:bg-gray-200 rounded"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {/* Folder Icon */}
        {isExpanded ? (
          <FolderOpen className="w-4 h-4 text-blue-500" />
        ) : (
          <Folder className="w-4 h-4 text-blue-500" />
        )}

        {/* Section Label */}
        <span className="flex-1 font-medium text-sm">{section.label}</span>

        {/* Action Buttons */}
        {!readOnly && (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAddQuestion}
              className="h-8 px-3"
              title="Add Question"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="ml-4 mt-1 space-y-1">
          {/* Questions */}
          {section.questions.map((question, qIdx) => (
            <QuestionNode
              key={question.id}
              question={question}
              path={[...path, qIdx]}
              selectedId={selectedId}
              onSelect={onSelectQuestion}
              onMove={(fromIdx, toIdx) => onMoveQuestion(path, fromIdx, toIdx)}
              readOnly={readOnly}
            />
          ))}

          {/* Child Sections */}
          {section.children.map((child, idx) => (
            <SectionNode
              key={child.id}
              section={child}
              path={[...path, idx]}
              selectedId={selectedId}
              depth={depth + 1}
              onSelect={onSelect}
              onSelectQuestion={onSelectQuestion}
              onMoveSection={onMoveSection}
              onMoveQuestion={onMoveQuestion}
              onAddQuestion={onAddQuestion}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
};
